import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { AppContext } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { isNativeiOS } from '../../lib/utils/platform';
import { isEmbroideryTrace, tutorialSampleImage, tutorialSampleFileName } from '../../config/appVariant';
import { isAppBusy, whenAppIdle } from './appBusy';
import {
  DEFAULT_TUTORIAL_ID,
  FIRST_LESSON_STEP,
  getTutorial,
  getTutorialSteps,
} from '../../tutorials';
import {
  readCompletedLessonIds,
  readLessonProgress,
  recordLessonCompleted,
  recordLessonStarted,
  recordLessonStep,
} from '../../lib/tutorial/tutorialProgress';

const TutorialContext = createContext();

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

// Tutorial steps for the converter learning path
const CONVERTER_TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Ready for Your Quick Tour?',
    description: 'Let us show you around **SvgTrace** in just **2 minutes**!\n\nYou\'ll learn how to:\n• **Upload or generate** your first image\n• **Convert** it to professional SVG\n• **Download** your finished file\n\n**Most users find this super helpful!** ⭐',
    target: null,
    position: 'center',
    showNext: true,
    showSkip: true,
    skipText: 'Skip Tour',
    nextText: 'Start Tour',
    encourageNext: true
  },
  {
    id: 'explore-tabs',
    title: 'Explore Your Files',
    description: 'Here you can see your **Recent Files** (uploaded & AI-generated images) and browse the **Community** gallery for inspiration.\n\nNow let\'s create your first SVG!',
    target: '.professional-tabs',
    position: 'bottom',
    showNext: true,
    highlight: true
  },
  {
    id: 'upload-or-generate',
    title: 'Choose Your Image Source',
    description: '**Select one option:**\n\n• **Upload** - Your designs, photos, artwork\n• **Generate** - AI creates custom designs\n\n**Best Results:** High contrast images with clear shapes\n\n*Or click "Next" to try with our sample image!*',
    target: '.sidebar-upload-dragger',
    position: 'right',
    showNext: true,
    nextText: 'Try Sample Image',
    waitForAction: 'file-uploaded',
    autoAdvance: true,
    highlight: true,
    actionRequired: false,
    multipleTargets: ['.sidebar-upload-dragger', '[data-action="generate"]'],
    customAction: 'load-sample-image'
  },
  {
    id: 'converter-welcome',
    title: 'Welcome to Converter',
    description: 'Transform your images into professional vector graphics in just a few clicks!\n\n• **AI-powered** conversion technology\n• **High-quality** scalable SVG output\n• **Perfect** for logos, illustrations & graphics\n\nLet\'s get started with a quick tour!',
    target: null,
    position: 'center',
    showNext: true,
    autoAdvance: false,
    highlight: false
  },
  {
    id: 'layout-1',
    title: 'Your Original Image',
    description: 'This is your uploaded PNG image.',
    target: '[data-tutorial="original-image-only"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'layout-3',
    title: 'Color Layers',
    description: 'Each color becomes a separate layer in your SVG.\n\n• **Preview** each color layer\n• **Remove** colors with trash icons\n• **Edit** hex codes directly',
    target: '[data-tutorial="color-layers"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'click-to-add-colors',
    title: '🎨 Click Image to Add Colors',
    description: '**Want to add more colors?** Simply **click anywhere on the original image** to sample and add that color!\n\n• Click any part of the image\n• The color gets added to your layers\n• Perfect for capturing missed details\n\n*Try it out - click the image to pick colors!*',
    target: '[data-tutorial="original-image-only"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'colors-strategy',
    title: 'Pro Tip: Color Optimization',
    description: '**For best results, reduce colors where possible:**\n\n• **Fewer colors** = cleaner cuts and simpler production\n\n*Pro advice: Start with fewer colors and add more if needed!*',
    target: '[data-tutorial="original-image-only"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'ai-toolkit-intro',
    title: '✨ AI Toolkit - Powerful Image Enhancement',
    description: 'Meet your **AI-powered assistant** at the bottom of the screen!\n\n**Three smart tools to enhance your image:**\n• **Retrace** - AI redraws low-res images for better quality\n• **Modify** - AI edits your image with simple text prompts\n• **Remove BG** - Instantly remove backgrounds\n\n*Look at the glowing toolbar at the bottom to see the AI Toolkit!*',
    target: '[data-tutorial="ai-toolkit-bar"]',
    position: 'center',
    showNext: true,
    highlight: true,
    noBackdrop: true
  },
  {
    id: 'ai-retrace',
    title: '🎨 Retrace - Enhance Quality',
    description: 'The **Retrace** button uses AI to redraw and sharpen your image.\n\n**Best for:**\n• Low-resolution images\n• Blurry or pixelated graphics\n• Images that need cleanup\n\n*Uses advanced AI to recreate your image with better clarity!*',
    target: '[data-tutorial="ai-retrace-button"]',
    position: 'center',
    showNext: true,
    highlight: true,
    noBackdrop: true
  },
  {
    id: 'ai-modify',
    title: '✏️ Modify - AI Image Editing',
    description: 'The **Modify** button lets you edit your image using simple text commands.\n\n**Example prompts:**\n• "Change text to say \'Hello\'"\n• "Make the background blue"\n• "Add a red border"\n\n*The AI understands natural language!*',
    target: '[data-tutorial="ai-modify-button"]',
    position: 'center',
    showNext: true,
    highlight: true,
    noBackdrop: true
  },
  {
    id: 'ai-remove-bg',
    title: '✂️ Remove BG - Instant Background Removal',
    description: 'The **Remove BG** button automatically removes the background from your image.\n\n**Perfect for:**\n• Isolating subjects\n• Creating transparent backgrounds\n• Cleaning up cluttered images\n\n*AI-powered precision in seconds!*',
    target: '[data-tutorial="ai-remove-bg-button"]',
    position: 'center',
    showNext: true,
    highlight: true,
    noBackdrop: true
  },
  
  {
    id: 'tools-1',
    title: 'Optional Tools',
    description: 'These tools help you fine-tune your conversion.',
    target: '[data-tutorial="conversion-settings"]',
    position: 'bottom',
    showNext: true,
    highlight: true
  },
  {
    id: 'layout-2',
    title: 'Live SVG Preview',
    description: 'This shows your converted SVG in real-time as you make changes.\n\n• **Scalable** - Vector graphics that work at any size\n• **Clean** - Professional quality output\n• **Live Updates** - See changes instantly',
    target: '[data-tutorial="svg-content"]',
    position: 'left',
    showNext: true,
    highlight: true
  },
  {
    id: 'download-step',
    title: 'Ready for the Editor?',
    description: 'Click **"Next"** to open the powerful **SVG Editor**.\n\nIn the editor, you\'ll learn to:\n• **Fine-tune** your vector graphics\n• **Use professional tools** for precision editing\n• **Export** in multiple formats',
    target: '[data-tutorial="download-button"]',
    position: 'top',
    showNext: true,
    highlight: true,
    waitForAction: 'editor-opened',
    autoAdvance: true,
    customAction: 'open-svg-editor'
  },
  {
    id: 'editor-welcome',
    title: 'Welcome to the SVG Editor!',
    description: '**You\'re now in the professional SVG Editor!**\n\nHere you can:\n• **Fine-tune** your vector graphics with precision tools\n• **Edit individual layers** and paths\n• **Export** in multiple formats (SVG, PNG, sharing)\n\nLet\'s explore the key features!',
    target: null,
    position: 'center',
    showNext: true,
    highlight: false
  },
  {
    id: 'editor-canvas',
    title: 'Your SVG Canvas',
    description: 'This is your **vector canvas** where you can edit your SVG.\n\n• **Zoom** and **pan** to navigate\n• **Click layers** to select them\n• **Use tools** to make precise edits',
    target: '[data-tutorial="svg-canvas"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'editor-toolbar',
    title: 'Your Design Toolkit',
    description: 'The **toolbar** has everything you need:\n\n• **Pan** - Navigate your canvas\n• **Crop** - Frame it perfectly\n• **Draw** - Freehand brush strokes\n• **Eraser** - Brush away extras\n• **Paint Bucket** - Fill areas instantly\n• **Vacuum** - Remove tiny specks\n• **Smart Cut** - Delete in one click\n• **Path Edit** - Adjust shapes\n• **Colors** - Match your vibe',
    target: '[data-tutorial="editor-toolbar"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'editor-layers',
    title: 'Layer Panel',
    description: 'Each **color becomes a layer** you can edit independently.\n\n• **Click** a layer to select it\n• **Change colors** and **reorder** layers',
    target: '[data-tutorial="layer-panel"]',
    position: 'left',
    showNext: true,
    highlight: true
  },
  {
    id: 'pan-tool',
    title: 'Pan',
    description: 'Move around your canvas like you\'re exploring a map!\n\n• **Drag to move** your view around\n• **Scroll to zoom** in and out',
    target: '[data-tutorial="pan-tool"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'crop-tool',
    title: 'Crop',
    description: 'Trim away the extra bits to make your design pop!\n\n• **Drag the corners** to frame your design perfectly\n• **Great for logos** and clean graphics',
    target: '[data-tutorial="crop-tool"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'draw-tool',
    title: 'Draw',
    description: 'Add freehand strokes to your design!\n\n• **Draw directly** on the canvas with a brush\n• **Adjust brush size** for fine or bold strokes\n• **Auto-vectorized** when you apply',
    target: '[data-tutorial="draw-tool"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'erase-tool',
    title: 'Eraser',
    description: 'Brush away the bits you don\'t need!\n\n• **Drag to erase** parts of your design\n• **Perfect for cleanup** and removing rough edges',
    target: '[data-tutorial="erase-tool"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'auto-fill-tool',
    title: 'Paint Bucket',
    description: 'Fill areas with a single click!\n\n• **Click any area** to fill it in\n• **Great for coloring** enclosed shapes\n• **Auto-vectorized** when you apply',
    target: '[data-tutorial="auto-fill-tool"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'vacuum-tool',
    title: 'Vacuum',
    description: 'Suck up all the tiny specks and dust!\n\n• **Removes anything smaller** than your brush size\n• **Adjust the brush** to control what gets cleaned',
    target: '[data-tutorial="vacuum-tool"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'smart-cut-tool',
    title: 'Smart Cut',
    description: 'Delete unwanted parts in one click - it\'s magic!\n\n• **Click on anything** to remove it instantly\n• **Clean up backgrounds** and extra elements',
    target: '[data-tutorial="smart-cut-tool"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'path-tool',
    title: 'Path Edit',
    description: 'Adjust your shapes to get them just right!\n\n• **Drag the dots** to reshape curves and lines\n• **Perfect for fine-tuning** logos and illustrations',
    target: '[data-tutorial="path-tool"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'color-tool',
    title: 'Colors',
    description: 'Change any color to match your vibe!\n\n• **Pick from the wheel** or type a color code\n• **Match your brand** colors perfectly',
    target: '[data-tutorial="color-tool"]',
    position: 'right',
    showNext: true,
    highlight: true
  },
  {
    id: 'export-button',
    title: 'Export Your Work',
    description: 'Ready to **save your professional SVG**?\n\nClick the **Export** button to:\n• **Download** as SVG or PNG\n• **Share** with a public link\n• **Copy** SVG code for web use',
    target: '[data-tutorial="export-button"]',
    position: 'left',
    showNext: true,
    highlight: true
  },
  {
    id: 'export-formats',
    title: 'Export Formats',
    description: '**Choose your export format:**\n\n• **SVG** - Scalable vector graphics (best quality)\n• **PNG** - Raster image with custom size\n• **SVG CODE** - Copy code for websites\n• **SHARE LINK** - Create a public link\n\nYou\'re now ready to create professional SVGs!',
    target: '[data-tutorial="export-formats"]',
    position: 'left',
    showNext: true,
    highlight: true
  },
  {
    id: 'success-professional',
    title: 'Congratulations! You\'re Now a Pro',
    description: '**You\'ve mastered professional SVG creation!**\n\n**What you\'ve learned:**\n• **Convert** images to scalable vector graphics\n• **Edit** with precision tools in the SVG Editor\n• **Export** in multiple professional formats\n\n**Your SVGs are ready for:**\n• **Print shops** • **Cutting machines** • **Web design**\n• **Sign making** • **Professional workflows**\n\n**Welcome to the world of professional vector graphics!**',
    target: null,
    position: 'center',
    showNext: false,
    showSkip: false,
    showComplete: true,
    highlight: false,
    large: true
  }
];


export const TutorialProvider = ({ children }) => {
  // 🚩 FEATURE FLAG: Set to true to enable tutorial system
  const TUTORIAL_ENABLED = true;
  
  // Access AppContext for sample image loading
  const {
    setImage64,
    setFileName,
    setImageSource,
    setTutorialMode,
    processTutorialImage
  } = useContext(AppContext);

  // Progress is stored against the signed-in account when there is one, so a
  // learner who finishes a lesson on the web sees it ticked off on mobile too.
  const { user } = useAuth();
  const uid = user?.uid;

  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tutorialType, setTutorialType] = useState('converter');
  const [completedTutorials, setCompletedTutorials] = useState(new Set());
  // Which interactive examples have been finished, by lesson id.
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [tutorialData, setTutorialData] = useState({});
  // True while waiting for the converter to finish tracing — hides the tutorial
  // overlay so the converter's own loading UI is unobstructed.
  const [isWaitingForConverter, setIsWaitingForConverter] = useState(false);
  // Which embroidery lesson is running. Set when the learner picks a design;
  // defaulted before that so the step counter isn't wrong on the opening slide.
  const [activeTutorialId, setActiveTutorialId] = useState(DEFAULT_TUTORIAL_ID);
  // Supplied by a component mounted inside the AppContext provider, which is
  // where the real image setters live.
  const designLoaderRef = useRef(null);
  const registerDesignLoader = useCallback((fn) => {
    designLoaderRef.current = fn;
  }, []);

  const getCurrentSteps = () => {
    switch (tutorialType) {
      case 'converter':
        return isEmbroideryTrace
          ? CONVERTER_TUTORIAL_STEPS.filter(step => step.id !== 'ai-remove-bg').map(step =>
            step.id === 'ai-toolkit-intro' ? { ...step, description: '**Two tools to enhance your image:**\n\n• **Retrace** - AI redraws low-resolution images for better quality\n• **Modify** - AI edits your image with simple text prompts' } : step)
          : CONVERTER_TUTORIAL_STEPS;
      case 'embroidery':
        return getTutorialSteps(activeTutorialId);
      default:
        return [];
    }
  };



  /** Jumps to a step by id — safer than relative steps when several things can advance. */
  const goToStepId = useCallback((stepId) => {
    const steps = getCurrentSteps();
    const index = steps.findIndex((step) => step.id === stepId);

    if (index === -1) {
      console.warn('⚠️ Tutorial: unknown step id', stepId);
      return;
    }

    setCurrentStep(index);
  }, [tutorialType, activeTutorialId]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerAction = useCallback((action, data = {}) => {
    const steps = getCurrentSteps();
    const currentStepData = steps[currentStep];
    
    console.log('🎯 triggerAction: Called with action:', action, 'currentStep:', currentStep);
    console.log('🎯 triggerAction: currentStepData.waitForAction:', currentStepData?.waitForAction);
    console.log('🎯 triggerAction: currentStepData.autoAdvance:', currentStepData?.autoAdvance);
    
    if (currentStepData?.waitForAction === action) {
      console.log('✅ triggerAction: Action matches! Will advance in 1.5s');
      if (currentStepData.autoAdvance) {
        const advanceFrom = currentStep;
        // Small delay for user to see the message
        setTimeout(() => {
          // Never advance over a loading screen, and never advance twice: if
          // something else already moved the tutorial on, this is stale.
          whenAppIdle(() => {
            setCurrentStep((prev) => {
              if (prev !== advanceFrom) {
                console.log('🎯 triggerAction: step already moved on, ignoring');
                return prev;
              }
              console.log('🎯 triggerAction: Advancing to next step');
              return prev + 1;
            });
          });
        }, 1500);
      }
    } else {
      console.log('⚠️ triggerAction: Action does not match current step requirements');
    }
    
    // Store action data for tutorial context
    setTutorialData(prev => ({ ...prev, [action]: data }));
  }, [currentStep, tutorialType, activeTutorialId]);

  const startTutorial = useCallback((type = 'converter', startFromStep = 0) => {
    if (!TUTORIAL_ENABLED) return;
    if (isNativeiOS() || window.innerWidth <= 768) return;
    
    setTutorialType(type);
    setCurrentStep(startFromStep);
    setIsActive(true);
    setTutorialData({});
    
    // Enable tutorial mode in the app context
    if (setTutorialMode) {
      setTutorialMode(true);
    }
    
    // Add tutorial class to body
    document.body.classList.add('tutorial-active');
    
    // Ensure sidebar is expanded for tutorial visibility
    const sidebar = document.querySelector('.sidebar');
    if (sidebar) {
      sidebar.classList.add('sidebar-persistent-expanded');
    }
  }, [setTutorialMode]);

  // Simplified function to handle tutorial sample image loading
  const handleTutorialSampleImage = useCallback(async () => {
    console.log('🎯 Tutorial: Loading sample image using simplified approach...');
    
    try {
      // Use the same sample image loading logic as the sidebar
      const sampleImagePath = tutorialSampleImage;
      const response = await fetch(sampleImagePath);
      
      let imageData = null;
      
      if (response.ok) {
        const blob = await response.blob();
        
        if (blob.size > 0) {
          imageData = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (e) => reject(e);
            reader.readAsDataURL(blob);
          });
          
          console.log('✅ Tutorial: Sample image loaded successfully');
        }
      }
      
      if (!imageData) {
        // Fallback: Generate a simple sample image
        console.log('⚠️ Tutorial: Using fallback sample image');
        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 200;
        const ctx = canvas.getContext('2d');
        
        // White background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, 200, 200);
        
        // Draw a simple star
        ctx.fillStyle = '#667eea';
        ctx.beginPath();
        ctx.moveTo(100, 20);
        ctx.lineTo(120, 70);
        ctx.lineTo(180, 70);
        ctx.lineTo(135, 110);
        ctx.lineTo(155, 180);
        ctx.lineTo(100, 140);
        ctx.lineTo(45, 180);
        ctx.lineTo(65, 110);
        ctx.lineTo(20, 70);
        ctx.lineTo(80, 70);
        ctx.closePath();
        ctx.fill();
        
        imageData = canvas.toDataURL('image/png');
      }
      
      // Set image data in context using the same approach as the sidebar
      if (setImage64) {
        setImage64(imageData);
      }
      if (setFileName) {
        setFileName(tutorialSampleFileName);
      }
      if (setImageSource) {
        setImageSource("uploaded");
      }
      
      console.log('🎯 Tutorial: Image data set in context, waiting for processing...');
      
    } catch (error) {
      console.error('❌ Tutorial: Error loading sample image:', error);
      // Still advance tutorial even if image loading fails
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, 1000);
    }
  }, [setImage64, setFileName, setImageSource, triggerAction]);

  const nextStep = useCallback(() => {
    const steps = getCurrentSteps();
    const currentStepData = steps[currentStep];
    
    console.log('🎯 nextStep: Called for step', currentStep, 'with data:', currentStepData);
    console.log('🎯 nextStep: customAction is:', currentStepData?.customAction);
    
    // Handle custom actions before advancing
    if (currentStepData?.customAction === 'load-sample-image') {
      console.log('🎯 nextStep: Automatically clicking Use Sample Image button');
      
      // Find and click the Use Sample Image button
      const sampleButton = document.querySelector('[data-id="sample"]');
      if (sampleButton) {
        console.log('✅ Tutorial: Found sample button, clicking it');
        sampleButton.click();
      } else {
        console.warn('⚠️ Tutorial: Sample button not found, trying alternative selector');
        // Try alternative selector for mobile
        const mobileSampleButton = document.querySelector('.mobile-more-item[data-sample="true"]');
        if (mobileSampleButton) {
          mobileSampleButton.click();
        } else {
          console.warn('⚠️ Tutorial: No sample button found, falling back to direct loading');
          handleTutorialSampleImage();
        }
      }
      
      // Don't advance step here - let the button click trigger the file-uploaded action
      return;
    }
    
    if (currentStepData?.customAction === 'open-svg-editor') {
      console.log('🎯 nextStep: Automatically clicking Next: Open SVG button');
      
      // Find and click the "Next: Open SVG" button
      const openSvgButton = document.querySelector('[data-tutorial="download-button"]');
      if (openSvgButton) {
        console.log('✅ Tutorial: Found Next: Open SVG button, clicking it');
        openSvgButton.click();
      } else {
        console.warn('⚠️ Tutorial: Next: Open SVG button not found, trying alternative selectors');
        // Try alternative selectors
        const submitButton = document.querySelector('button[type="submit"]');
        // Find button containing "Next" text (valid JS approach instead of invalid :contains selector)
        const allButtons = document.querySelectorAll('button');
        const nextButton = Array.from(allButtons).find(btn => btn.textContent.includes('Next'));
        if (submitButton) {
          submitButton.click();
        } else if (nextButton) {
          nextButton.click();
        } else {
          console.warn('⚠️ Tutorial: No suitable button found, advancing tutorial anyway');
          // Still advance to next step if button not found
          setTimeout(() => {
            setCurrentStep(prev => prev + 1);
          }, 1000);
        }
      }
      
      // Don't advance step here - let the button click trigger the editor-opened action
      return;
    }
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTutorial();
    }
  }, [currentStep, setCurrentStep, handleTutorialSampleImage]);

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((stepIndex) => {
    const steps = getCurrentSteps();
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
    }
  }, [tutorialType, activeTutorialId]); // eslint-disable-line react-hooks/exhaustive-deps

  const skipTutorial = useCallback(() => {
    setIsActive(false);
    document.body.classList.remove('tutorial-active');
    
    // Disable tutorial mode
    if (setTutorialMode) {
      setTutorialMode(false);
    }
    
    // Don't collapse sidebar when skipping - let it stay in its current state
    // Users might want to keep it expanded for their workflow
  }, [setTutorialMode]);

  const completeTutorial = useCallback(() => {
    setCompletedTutorials(prev => new Set([...prev, tutorialType]));
    setIsActive(false);
    document.body.classList.remove('tutorial-active');
    
    // Disable tutorial mode
    if (setTutorialMode) {
      setTutorialMode(false);
    }
    
    // Remove sidebar expansion
    const sidebar = document.querySelector('.sidebar');
    if (sidebar) {
      sidebar.classList.remove('sidebar-persistent-expanded');
    }
    
    // Store completion in localStorage
    const completed = JSON.parse(localStorage.getItem('svgtrace_completed_tutorials') || '[]');
    if (!completed.includes(tutorialType)) {
      completed.push(tutorialType);
      localStorage.setItem('svgtrace_completed_tutorials', JSON.stringify(completed));
    }

    // Credit the specific example, not just "the embroidery tutorial" — the
    // picker and the home card both need to know which lessons are done.
    if (tutorialType === 'embroidery' && activeTutorialId) {
      recordLessonCompleted({
        uid,
        lessonId: activeTutorialId,
        lessonName: getTutorial(activeTutorialId)?.design?.name,
        totalSteps: getTutorialSteps(activeTutorialId).length,
      });
      setCompletedLessons(prev => new Set([...prev, activeTutorialId]));
    }
  }, [tutorialType, setTutorialMode, activeTutorialId, uid]);

  // Function to load a sample image for the tutorial
  const loadSampleImage = useCallback(async (design) => {
    console.log('🎯 Tutorial: Loading sample image...');
    console.log('🎯 Tutorial: loadSampleImage function STARTED');
    // Hide the tutorial overlay immediately so the converter's loading UI shows cleanly
    setIsWaitingForConverter(true);

    // The picker passes the chosen practice design; fall back to the variant's
    // default sample when the loader is called from elsewhere.
    const imagePath = design?.image || tutorialSampleImage;
    const imageName = design?.fileName || tutorialSampleFileName;

    try {
      // The image is at public/assets/tutorial_sample.png
      // In React dev server, public files are served from root
      const possiblePaths = [
        imagePath,  // This should work with CRA/CRACO
        `${process.env.PUBLIC_URL}${imagePath}`,  // With PUBLIC_URL if set
        `${window.location.origin}${imagePath}`,  // Full URL
        `.${imagePath}`  // Relative path as fallback
      ];
      
      console.log('🎯 Tutorial: PUBLIC_URL is:', process.env.PUBLIC_URL);
      console.log('🎯 Tutorial: Window origin is:', window.location.origin);
      
      // Quick test to verify public folder serving
      try {
        const testResponse = await fetch('/test.txt');
        console.log('🔍 Tutorial: Test file fetch status:', testResponse.status);
        if (testResponse.ok) {
          const testContent = await testResponse.text();
          console.log('✅ Tutorial: Public folder IS being served. Test file content:', testContent);
        } else {
          console.warn('⚠️ Tutorial: Public folder may not be served correctly. Test file status:', testResponse.status);
        }
      } catch (testErr) {
        console.error('❌ Tutorial: Failed to fetch test file from public folder:', testErr);
      }
      
      let imageData = null;
      let successPath = null;
      
      for (const sampleImagePath of possiblePaths) {
        try {
          console.log('📂 Tutorial: Trying to fetch image from:', sampleImagePath);
          
          const response = await fetch(sampleImagePath, {
            method: 'GET',
            headers: {
              'Accept': 'image/*'
            }
          });
          
          console.log('📡 Tutorial: Fetch response:', {
            status: response.status,
            statusText: response.statusText,
            ok: response.ok,
            type: response.type,
            url: response.url,
            contentType: response.headers.get('content-type')
          });
          
          if (response.ok && response.status === 200) {
            const blob = await response.blob();
            console.log('📦 Tutorial: Blob created, size:', blob.size, 'type:', blob.type);
            
            if (blob.size === 0) {
              console.warn('⚠️ Tutorial: Blob is empty, skipping this path');
              continue;
            }
            
            imageData = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (e) => {
                console.log('📖 Tutorial: FileReader onload triggered');
                console.log('📖 Tutorial: Result length:', e.target.result?.length);
                console.log('📖 Tutorial: Result preview:', e.target.result?.substring(0, 100));
                resolve(e.target.result);
              };
              reader.onerror = (e) => {
                console.error('❌ Tutorial: FileReader error:', e);
                reject(e);
              };
              console.log('📖 Tutorial: Starting FileReader.readAsDataURL');
              reader.readAsDataURL(blob);
            });
            
            successPath = sampleImagePath;
            console.log('✅ Tutorial: Successfully loaded image from:', successPath);
            break;
          } else {
            console.warn('⚠️ Tutorial: Response not OK for path:', sampleImagePath, 'Status:', response.status);
          }
        } catch (pathError) {
          console.warn('⚠️ Tutorial: Failed to load from path:', sampleImagePath, pathError.message);
          continue;
        }
      }
      
      // Alternative approach: Try loading via Image element if fetch failed
      if (!imageData) {
        console.log('🔄 Tutorial: Trying alternative Image element loading approach...');
        
        try {
          const testImagePath = imagePath;
          imageData = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = function() {
              console.log('✅ Tutorial: Image element loaded successfully!');
              // Convert image to data URL using canvas
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              const dataUrl = canvas.toDataURL('image/png');
              console.log('✅ Tutorial: Converted to data URL, length:', dataUrl.length);
              resolve(dataUrl);
            };
            img.onerror = function(e) {
              console.error('❌ Tutorial: Image element failed to load:', e);
              reject(e);
            };
            console.log('🔄 Tutorial: Setting img.src to:', testImagePath);
            img.src = testImagePath;
          });
          
          if (imageData) {
            successPath = 'Image element approach';
            console.log('✅ Tutorial: Successfully loaded via Image element!');
          }
        } catch (imgError) {
          console.error('❌ Tutorial: Image element approach also failed:', imgError);
        }
      }
      
      if (!imageData) {
        console.warn('⚠️ Tutorial: No image loaded from any method, using fallback sample image');
        console.warn('⚠️ Tutorial: Check DevTools Network tab to see if /assets/tutorial_sample.png is loading');
        console.warn('⚠️ Tutorial: The file exists at: public/assets/tutorial_sample.png (139KB, 1500x1500 PNG)');
        // Fallback: Simple star shape that works well for SVG conversion
        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 200;
        const ctx = canvas.getContext('2d');
        
        // White background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, 200, 200);
        
        // Draw a simple star
        ctx.fillStyle = '#667eea';
        ctx.beginPath();
        ctx.moveTo(100, 20);
        ctx.lineTo(120, 70);
        ctx.lineTo(180, 70);
        ctx.lineTo(135, 110);
        ctx.lineTo(155, 180);
        ctx.lineTo(100, 140);
        ctx.lineTo(45, 180);
        ctx.lineTo(65, 110);
        ctx.lineTo(20, 70);
        ctx.lineTo(80, 70);
        ctx.closePath();
        ctx.fill();
        
        imageData = canvas.toDataURL('image/png');
        console.log('✅ Tutorial: Generated fallback star image');
      }
      
       console.log('📸 Tutorial sample image loaded from:', successPath || 'fallback');
       console.log('📸 Tutorial: Final imageData length:', imageData?.length);
       console.log('📸 Tutorial: Final imageData type:', typeof imageData);
       console.log('📸 Tutorial: ImageData starts with data:image?', imageData?.startsWith('data:image'));
       console.log('📸 Tutorial: ImageData preview:', imageData?.substring(0, 100) + '...');
      
      // Set image data in context
      if (setImage64) {
        console.log('🎯 Tutorial: About to call setImage64 with data length:', imageData?.length);
        console.log('🎯 Tutorial: setImage64 function:', typeof setImage64);
        setImage64(imageData);
        console.log('✅ Tutorial: image64 set in context');
      } else {
        console.error('❌ Tutorial: setImage64 function not available!');
      }
      if (setFileName) {
        setFileName(imageName);
        console.log('✅ Tutorial: fileName set in context');
      }
      if (setImageSource) {
        setImageSource('uploaded');
        console.log('✅ Tutorial: imageSource set to "uploaded"');
      }
      
      // Use the new reliable processing approach
      // NOTE: Do NOT call triggerAction('file-uploaded') here — it would advance to
      // converter-welcome after only 1500ms, before tracing is complete. Instead,
      // the polling below advances the step only once the converter is fully ready.
      if (processTutorialImage) {
        console.log('🎯 Tutorial: Processing image using reliable method');
        console.log('🎯 Tutorial: processTutorialImage function available:', typeof processTutorialImage);
        console.log('🎯 Tutorial: About to call processTutorialImage with imageData length:', imageData?.length);
        
        try {
          const result = await processTutorialImage(imageData);
          console.log('✅ Tutorial: Image processed successfully', result);
          
          // Poll until the SVG conversion is fully done.
          // We check that the "Next: Open SVG" submit button exists and is NOT
          // disabled — it stays disabled the entire time previewLoading is true,
          // regardless of whether the loading overlay has rendered yet (the overlay
          // has a 150ms delay, so checking for its absence is unreliable).
          const POLL_INTERVAL = 500;
          const MAX_WAIT_MS = 60000;
          let elapsed = 0;

            const waitForConverter = () => {
            const submitBtn = document.querySelector('[data-tutorial="download-button"]');
            const converterReady = document.querySelector('[data-tutorial="original-image-only"]');
            const svgDone = submitBtn && !submitBtn.disabled;
            if (converterReady && svgDone) {
              console.log('🎯 Tutorial: SVG conversion complete, advancing to converter-welcome');
              setIsWaitingForConverter(false);
              setCurrentStep(prev => prev + 1);
            } else if (elapsed < MAX_WAIT_MS) {
              elapsed += POLL_INTERVAL;
              setTimeout(waitForConverter, POLL_INTERVAL);
            } else {
              console.warn('⚠️ Tutorial: Converter timed out, advancing anyway');
              setIsWaitingForConverter(false);
              setCurrentStep(prev => prev + 1);
            }
          };

          // Short initial delay to let the converter mount and start loading
          setTimeout(waitForConverter, 1500);
          
        } catch (processingError) {
          console.error('❌ Tutorial: Failed to process image:', processingError);
          setTimeout(() => {
            setIsWaitingForConverter(false);
            setCurrentStep(prev => prev + 1);
          }, 1000);
        }
      } else {
        console.error('❌ Tutorial: processTutorialImage not available in context');
        setTimeout(() => {
          setIsWaitingForConverter(false);
          setCurrentStep(prev => prev + 1);
        }, 1000);
      }
      
    } catch (error) {
      console.error('❌ Tutorial: Failed to load sample image:', error);
      setTimeout(() => {
        setIsWaitingForConverter(false);
        setCurrentStep(prev => prev + 1);
      }, 1000);
    }
  }, [setImage64, setFileName, setImageSource, processTutorialImage, triggerAction]);

  /**
   * Loads a practice design for the embroidery walkthrough.
   *
   * EmbroideryTrace opens the Converter so the learner can review thread
   * colours before the Stitch Editor. This waits until that screen is ready;
   * the prelude then asks them to click Next: Stitch Editor.
   */
  const loadTutorialDesign = useCallback(async (design) => {
    const loader = designLoaderRef.current;

    if (!loader) {
      console.error('❌ Tutorial: no design loader registered');
      return;
    }

    // The card the learner picked decides which lesson runs from here on.
    if (design?.tutorialId) {
      setActiveTutorialId(design.tutorialId);

      recordLessonStarted({
        uid,
        lessonId: design.tutorialId,
        lessonName: design.name,
        totalSteps: getTutorialSteps(design.tutorialId).length,
      });
    }

    try {
      // Jump by id rather than stepping forward. The upload pipeline also fires
      // a 'file-uploaded' action, and two relative advances would race into one
      // step too many — which is how the "analysing" step used to get skipped.
      goToStepId('emb-processing');
      setIsWaitingForConverter(true);

      await loader(design);

      const POLL_INTERVAL = 400;
      const MAX_WAIT_MS = 120000;
      const startedAt = Date.now();

      const converterReady = () => {
        const submitBtn = document.querySelector('[data-tutorial="download-button"]');
        const layers = document.querySelector('[data-tutorial="color-layers"]');
        return !!(layers && submitBtn && !submitBtn.disabled);
      };

      const waitForConverter = () => {
        if (converterReady() && !isAppBusy()) {
          setIsWaitingForConverter(false);
          goToStepId('emb-converter-colors');
          return;
        }

        if (Date.now() - startedAt < MAX_WAIT_MS) {
          setTimeout(waitForConverter, POLL_INTERVAL);
          return;
        }

        console.warn('⚠️ Tutorial: Converter did not settle in time');
        setIsWaitingForConverter(false);
        if (converterReady()) goToStepId('emb-converter-colors');
      };

      setTimeout(waitForConverter, 800);
    } catch (error) {
      console.error('❌ Tutorial: Failed to load practice design:', error);
      setIsWaitingForConverter(false);
    }
  }, [goToStepId, uid]);

  const getCurrentStepData = () => {
    const steps = getCurrentSteps();
    return steps[currentStep] || null;
  };

  const getTutorialProgress = () => {
    const steps = getCurrentSteps();
    return {
      current: currentStep + 1,
      total: steps.length,
      percentage: Math.round(((currentStep + 1) / steps.length) * 100)
    };
  };

  const hasCompletedTutorial = (type) => {
    return completedTutorials.has(type);
  };

  const hasCompletedLesson = useCallback(
    (lessonId) => completedLessons.has(lessonId),
    [completedLessons],
  );

  // Load completed tutorials from localStorage on mount
  React.useEffect(() => {
    const completed = JSON.parse(localStorage.getItem('svgtrace_completed_tutorials') || '[]');
    setCompletedTutorials(new Set(completed));
    setCompletedLessons(new Set(readCompletedLessonIds()));
  }, []);

  // Note how far the learner gets. Recorded per step rather than only at the
  // end, because the interesting lessons are the ones people abandon.
  React.useEffect(() => {
    if (!isActive || tutorialType !== 'embroidery' || !activeTutorialId) return;

    const steps = getTutorialSteps(activeTutorialId);
    const step = steps[currentStep];
    if (!step) return;

    recordLessonStep({
      uid,
      lessonId: activeTutorialId,
      stepId: step.id,
      stepIndex: currentStep,
      totalSteps: steps.length,
    });
  }, [isActive, tutorialType, activeTutorialId, currentStep, uid]);

  // After the learner clicks Next: Stitch Editor, hold until the editor is
  // actually up. editor-opened also advances this step; the poll is the
  // fallback if that signal is missed.
  React.useEffect(() => {
    if (!isActive || tutorialType !== 'embroidery') return undefined;
    const step = getTutorialSteps(activeTutorialId)[currentStep];
    if (step?.id !== 'emb-opening-editor') return undefined;

    const POLL_INTERVAL = 400;
    const MAX_WAIT_MS = 120000;
    const startedAt = Date.now();
    let timer = null;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      const canvasReady = !!document.querySelector('[data-tutorial="stitch-canvas"]');
      if (canvasReady && !isAppBusy()) {
        setCurrentStep(FIRST_LESSON_STEP);
        return;
      }
      if (Date.now() - startedAt < MAX_WAIT_MS) {
        timer = setTimeout(tick, POLL_INTERVAL);
        return;
      }
      console.warn('⚠️ Tutorial: Stitch Editor did not settle in time');
      if (canvasReady) setCurrentStep(FIRST_LESSON_STEP);
    };

    timer = setTimeout(tick, 800);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isActive, tutorialType, currentStep, activeTutorialId]);

  // Expose triggerAction to window for external access (like from Home component)
  React.useEffect(() => {
    if (isActive) {
      window.tutorialTriggerAction = triggerAction;
      window.tutorialSetStep = (stepIndex) => {
        console.log('🎯 Tutorial: Setting step to:', stepIndex);
        setCurrentStep(stepIndex);
      };
      window.tutorialGetCurrentStep = () => {
        console.log('🎯 Tutorial: Current step:', currentStep);
        return currentStep;
      };
      window.tutorialGetCurrentStepId = () => getCurrentSteps()[currentStep]?.id;
      window.tutorialTestSampleImage = () => {
        console.log('🎯 Tutorial: Testing sample image load directly');
        loadSampleImage();
      };
      console.log('🎯 Tutorial: triggerAction exposed to window');
    } else {
      delete window.tutorialTriggerAction;
      delete window.tutorialGetCurrentStepId;
    }
    
    // Cleanup on unmount
    return () => {
      delete window.tutorialTriggerAction;
      delete window.tutorialGetCurrentStepId;
    };
  }, [isActive, triggerAction, currentStep, tutorialType, activeTutorialId]);

  const value = {
    // State
    isActive,
    currentStep,
    tutorialType,
    tutorialData,
    isWaitingForConverter,
    activeTutorialId,
    completedLessons,
    
    // Actions
    startTutorial,
    nextStep,
    previousStep,
    goToStep,
    skipTutorial,
    completeTutorial,
    triggerAction,
    loadSampleImage,
    loadTutorialDesign,
    registerDesignLoader,
    
    // Getters
    getCurrentStepData,
    getTutorialProgress,
    hasCompletedTutorial,
    hasCompletedLesson,
    readLessonProgress,
    
    // Config
    TUTORIAL_ENABLED
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
